import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import { getUpcomingWork, useTimerStore, type TimerState } from '../../stores/timer-store'
import { cn } from '../../lib/utils'

function formatMMSS(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds))
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m}:${sec.toString().padStart(2, '0')}`
}

const KIND_LABEL_KEY: Record<string, string> = {
  normal: 'workoutTimer.kind.normal',
  warmup: 'workoutTimer.kind.warmup',
  dropset: 'workoutTimer.kind.dropset',
  failure: 'workoutTimer.kind.failure',
}

export function TimerStage() {
  const { t } = useTranslation()
  const phase = useTimerStore((s) => s.phase)
  const phaseStartedAt = useTimerStore((s) => s.phaseStartedAt)
  const phaseDurationMs = useTimerStore((s) => s.phaseDurationMs)
  const paused = useTimerStore((s) => s.paused)
  const pausedRemainingMs = useTimerStore((s) => s.pausedRemainingMs)
  const templates = useTimerStore((s) => s.templates)
  const templateIdx = useTimerStore((s) => s.templateIdx)
  const setIdx = useTimerStore((s) => s.setIdx)
  const repIdx = useTimerStore((s) => s.repIdx)
  const phasePending = useTimerStore((s) => s.phasePending)
  const currentSide = useTimerStore((s) => s.currentSide)

  const isBetweenExercises = phase === 'between_exercises'
  // Two kinds of pause between blocks: a counted one, which reads like any
  // other rest, and the open-ended wait, which counts up until the athlete is
  // ready. Only the second has no duration to count down.
  const isOpenEndedPause = isBetweenExercises && phaseDurationMs == null

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (paused || phase === 'idle' || phase === 'armed' || phase === 'completed') return
    // The open-ended pause still needs the interval: its clock counts up even
    // though `phaseDurationMs` is null.
    if (phaseDurationMs == null && phase !== 'between_exercises') return
    const id = window.setInterval(() => setNow(Date.now()), 250)
    return () => window.clearInterval(id)
  }, [paused, phase, phaseDurationMs])

  const tpl = templates[templateIdx]
  if (!tpl) return null

  const remainingMs = (() => {
    if (paused && pausedRemainingMs != null) return pausedRemainingMs
    if (phaseDurationMs == null || phaseStartedAt == null) return null
    return Math.max(0, phaseDurationMs - (now - phaseStartedAt))
  })()

  const elapsedSinceStartMs = phaseStartedAt != null ? Math.max(0, now - phaseStartedAt) : 0

  const isPrep = phase === 'prep'
  const isManualWork = phase === 'work' && tpl.manual
  const totalMs = phaseDurationMs ?? 1
  const progressPct = remainingMs == null ? 100 : (1 - remainingMs / totalMs) * 100

  const remainingSec = remainingMs != null ? Math.max(0, Math.ceil(remainingMs / 1000)) : 0
  const showBigSeconds =
    !isPrep && !isManualWork && !isOpenEndedPause && remainingMs != null && remainingMs > 0 && remainingMs < 10_000

  const barColor = (() => {
    if (isPrep) return 'bg-muted-foreground/60'
    if (phase === 'work') return 'bg-red-500'
    if (phase === 'side_change') return 'bg-amber-500'
    if (phase === 'rest' || phase === 'set_rest') return 'bg-blue-500'
    if (isBetweenExercises) return 'bg-blue-500/50'
    return 'bg-green-500'
  })()

  const sideLabel = currentSide
    ? currentSide === 'A'
      ? t('workoutTimer.sideA', 'Side A')
      : t('workoutTimer.sideB', 'Side B')
    : null

  const phaseLabel = (() => {
    if (isPrep) return t('workoutTimer.phase.prep', 'Preparati!!')
    if (phase === 'side_change') return t('workoutTimer.phase.sideChange', 'Switch sides')
    if (phase === 'work' && tpl.manual) {
      return sideLabel
        ? `${t('workoutTimer.phase.workManual', 'Work — tap when done')} · ${sideLabel}`
        : t('workoutTimer.phase.workManual', 'Work — tap when done')
    }
    if (phase === 'work') {
      return sideLabel ? `${t('workoutTimer.phase.work', 'Work')} · ${sideLabel}` : t('workoutTimer.phase.work', 'Work')
    }
    if (phase === 'rest') return t('workoutTimer.phase.rest', 'Rest')
    if (phase === 'set_rest') return t('workoutTimer.phase.setRest', 'Rest between sets')
    if (isBetweenExercises) return t('workoutTimer.phase.betweenExercises', 'Rest between exercises')
    if (phase === 'completed') return t('workoutTimer.phase.completed', 'Completed')
    return ''
  })()

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex flex-col min-w-0">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {t(KIND_LABEL_KEY[tpl.kind] ?? KIND_LABEL_KEY.normal)}
          </span>
          <h2 className="text-base font-semibold leading-tight truncate">
            {tpl.title ?? t('workoutTimer.untitledTemplate', 'Template')}
          </h2>
          {tpl.short_description && (
            <span className="text-xs text-muted-foreground">{tpl.short_description}</span>
          )}
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {t('workoutTimer.setCounter', 'Set {{current}}/{{total}}', {
            current: setIdx + 1,
            total: tpl.sets,
          })}
        </span>
      </div>

      <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
        <div
          className={cn('h-full transition-[width] duration-150 ease-linear', barColor)}
          style={{ width: isManualWork || isOpenEndedPause ? '100%' : `${progressPct}%` }}
        />
      </div>

      <div className="flex flex-col items-center gap-1 py-2">
        <span
          className={cn(
            'text-xs uppercase tracking-wide font-medium',
            isPrep && 'text-muted-foreground animate-pulse',
            !isPrep && phase === 'work' && 'text-red-500',
            !isPrep && phase === 'side_change' && 'text-amber-600',
            !isPrep && (phase === 'rest' || phase === 'set_rest') && 'text-blue-500',
            isBetweenExercises && 'text-blue-500',
          )}
        >
          {phaseLabel}
        </span>
        {showBigSeconds ? (
          <BigSecondsCountdown seconds={remainingSec} />
        ) : (
          <span
            data-testid={isOpenEndedPause ? 'between-exercises-elapsed' : undefined}
            className={cn(
              'text-5xl font-bold tabular-nums',
              isPrep && 'text-muted-foreground',
              isBetweenExercises && 'text-blue-500',
            )}
          >
            {isManualWork
              ? '—'
              : isOpenEndedPause
                ? formatMMSS(elapsedSinceStartMs / 1000)
                : formatMMSS((remainingMs ?? 0) / 1000)}
          </span>
        )}
        {tpl.reps_per_set > 1 && !isPrep && (
          <span className="text-xs text-muted-foreground">
            {t('workoutTimer.repCounter', 'Rep {{current}}/{{total}}', {
              current: repIdx + 1,
              total: tpl.reps_per_set,
            })}
          </span>
        )}
        {tpl.manual && tpl.reps_target != null && phase === 'work' && (
          <span className="text-xs text-muted-foreground">
            {t('workoutTimer.repsTarget', '{{count}} reps', { count: tpl.reps_target })}
          </span>
        )}
      </div>

      <UpcomingPreview
        templates={templates}
        templateIdx={templateIdx}
        setIdx={setIdx}
        repIdx={repIdx}
        phase={phase}
        phasePending={phasePending}
        currentSide={currentSide}
      />
    </div>
  )
}

interface UpcomingPreviewProps {
  templates: TimerState['templates']
  templateIdx: number
  setIdx: number
  repIdx: number
  phase: TimerState['phase']
  phasePending: TimerState['phasePending']
  currentSide: TimerState['currentSide']
}

function UpcomingPreview({ templates, templateIdx, setIdx, repIdx, phase, phasePending, currentSide }: UpcomingPreviewProps) {
  const { t } = useTranslation()
  if (phase === 'idle' || phase === 'armed' || phase === 'prep' || phase === 'completed' || phase === 'between_exercises') return null
  const upcoming = getUpcomingWork({
    templates,
    templateIdx,
    setIdx,
    repIdx,
    phase,
    phasePending,
    currentSide,
  } as TimerState)
  if (!upcoming) return null
  const upcomingTpl = templates[upcoming.templateIdx]
  if (!upcomingTpl) return null
  const title = upcomingTpl.title ?? t('workoutTimer.untitledTemplate', 'Template')
  const sideSuffix = upcoming.side
    ? upcoming.side === 'A'
      ? ` · ${t('workoutTimer.sideA', 'Side A')}`
      : ` · ${t('workoutTimer.sideB', 'Side B')}`
    : ''
  const label = upcoming.isNewExercise
    ? `${title}${sideSuffix}`
    : upcomingTpl.reps_per_set > 1
      ? `${t('workoutTimer.upNextSetRep', 'Set {{set}} · Rep {{rep}}', {
          set: upcoming.setIdx + 1,
          rep: upcoming.repIdx + 1,
        })}${sideSuffix}`
      : `${t('workoutTimer.upNextSet', 'Set {{set}}', { set: upcoming.setIdx + 1 })}${sideSuffix}`
  return (
    <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground border-t pt-2">
      <ChevronRight className="size-3" />
      <span>{t('workoutTimer.upNext', 'Up next')}:</span>
      <span className="font-medium text-foreground truncate">{label}</span>
    </div>
  )
}

/** Last-10-seconds large orange seconds with crossfade between digit changes. */
function BigSecondsCountdown({ seconds }: { seconds: number }) {
  const [displayed, setDisplayed] = useState(seconds)
  const [fading, setFading] = useState(false)

  useEffect(() => {
    if (seconds === displayed) return
    setFading(true)
    const id = window.setTimeout(() => {
      setDisplayed(seconds)
      setFading(false)
    }, 150)
    return () => window.clearTimeout(id)
  }, [seconds, displayed])

  return (
    <span
      data-testid="big-seconds"
      className={cn(
        'text-7xl font-bold tabular-nums text-orange-500 transition-opacity duration-150',
        fading ? 'opacity-0' : 'opacity-100',
      )}
    >
      {displayed}
    </span>
  )
}
